import { AppShell, Sidebar, UploadPage } from '@suvoda-uat/ui-suvoda';
import type { TrialStatus } from '@suvoda-uat/domain';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { LifecycleTopBar } from '../components/LifecycleTopBar.js';
import { UploadView } from '../components/UploadView.js';
import type { MasterTemplateVersionInfo, SlotName } from '../lib/api-client.js';
import { signOut } from '../lib/auth.js';
import { navTargetFor, visibleNavItems } from '../lib/nav.js';
import {
  slotFilename,
  useCreateTrialFromFirstUpload,
  useDeleteDocument,
  useMasterTemplateVersions,
  useSetRandomization,
  useStartGeneration,
  useTrial,
  useTrialPack,
  useUploadDocument,
} from '../lib/queries.js';
import {
  createLifecycleBeforeLoad,
  guardLifecycleRoute,
  type LifecycleDestination,
} from '../lib/lifecycle.js';

const lifecycleBeforeLoad = createLifecycleBeforeLoad('/upload');

/**
 * F-020 (R1): /upload without a trialId is the NO-TRIAL mode — the entry point
 * of "Create New Project". The lifecycle guard only applies once a trial
 * exists; the no-trial mode renders the empty upload screen and the first
 * successful upload creates the Trial (stage → PUT → CreateTrial).
 */
export const uploadBeforeLoad = async (input: { search: { trialId: string } }): Promise<void> => {
  if (input.search.trialId.trim() === '') return;
  await lifecycleBeforeLoad(input);
};

export const Route = createFileRoute('/upload')({
  validateSearch: (search: Record<string, unknown>): { trialId: string } => ({
    trialId: typeof search['trialId'] === 'string' ? search['trialId'] : '',
  }),
  beforeLoad: uploadBeforeLoad,
  component: UploadRoute,
});

const errorMessage = (error: Error | null): string | null => error?.message ?? null;

export const uploadDestinationForTrialStatus = (
  status: TrialStatus | undefined,
  trialId: string,
): LifecycleDestination | null => {
  if (status === undefined) return null;

  const decision = guardLifecycleRoute({ status, trialId, currentPath: '/upload' });
  return decision.kind === 'redirect' ? decision.destination : null;
};

/** Promote the search params to the freshly created trial (R1, replace). */
export const firstUploadSuccessDestination = (trialId: string) => ({
  to: '/upload' as const,
  search: { trialId },
  replace: true,
});

/** The picker default: the catalogue's isLatest version (US1-6/FR-009). */
export const defaultTemplateVersion = (
  versions: readonly MasterTemplateVersionInfo[],
): string | undefined => versions.find((v) => v.isLatest)?.version;

function UploadRoute() {
  const navigate = useNavigate();
  const { trialId } = Route.useSearch();
  const hasTrial = trialId !== '';
  const trial = useTrial(trialId);
  const pack = useTrialPack(trialId);
  const upload = useUploadDocument(trialId);
  const remove = useDeleteDocument(trialId);
  const start = useStartGeneration(trialId);
  const createFromFirstUpload = useCreateTrialFromFirstUpload();
  const templateVersions = useMasterTemplateVersions();
  const setRandomization = useSetRandomization(trialId);

  // R6: non-blocking picker — defaults to latest, user-overridable until the
  // trial exists, then locked (the version is a create-time input).
  const [pickedVersion, setPickedVersion] = useState<string | undefined>(undefined);
  const versions = templateVersions.data?.items ?? [];
  const selectedTemplateVersion = hasTrial
    ? (trial.data?.masterTemplateVersion ?? '')
    : (pickedVersion ?? defaultTemplateVersion(versions) ?? '');

  const version = trial.data?.version ?? 0;
  const busy =
    upload.isPending ||
    remove.isPending ||
    start.isPending ||
    createFromFirstUpload.isPending ||
    setRandomization.isPending;
  const mutationError =
    upload.error ??
    remove.error ??
    start.error ??
    createFromFirstUpload.error ??
    setRandomization.error;

  useEffect(() => {
    if (!hasTrial) return;
    const destination = uploadDestinationForTrialStatus(trial.data?.status, trialId);
    if (destination !== null) void navigate({ ...destination, replace: true });
  }, [hasTrial, navigate, trial.data?.status, trialId]);

  // F-020 (R1): the FIRST slot gesture (ANY slot) creates the Trial by
  // adopting the staged upload; subsequent uploads use the per-trial path.
  const onSelect = (slot: SlotName) => (file: File) => {
    if (hasTrial) {
      upload.mutate({ trialId, slot, file, version });
      return;
    }
    createFromFirstUpload.mutate(
      { slot, file, masterTemplateVersion: selectedTemplateVersion },
      {
        onSuccess: (created) => void navigate(firstUploadSuccessDestination(created.id)),
      },
    );
  };

  return (
    <AppShell
      sidebar={
        <Sidebar
          items={visibleNavItems()}
          activeId="uat"
          searchPlaceholder="What are you looking for?"
          onNavigate={(id) => {
            const to = navTargetFor(id);
            if (to !== undefined) void navigate({ to });
          }}
          onSignOut={() => signOut()}
        />
      }
      topbar={<LifecycleTopBar currentStep="upload" trial={trial.data} />}
      contentClassName="px-8 pb-7 pt-3"
    >
      <UploadPage onCancel={() => navigate({ to: '/' })}>
        <UploadView
          ursFilename={pack.data ? slotFilename(pack.data.urs) : null}
          trsFilename={pack.data ? slotFilename(pack.data.trs) : null}
          rolesMatrixFilename={pack.data?.rolesMatrix ? slotFilename(pack.data.rolesMatrix) : null}
          randomizationAddendumFilename={
            pack.data ? slotFilename(pack.data.randomizationAddendum) : null
          }
          randomizationEnabled={pack.data?.randomizationEnabled ?? false}
          canProcess={pack.data?.status === 'ready' && trial.data?.status === 'setting_up'}
          busy={busy}
          errorMessage={errorMessage(mutationError)}
          templateVersions={versions}
          selectedTemplateVersion={selectedTemplateVersion}
          onSelectTemplateVersion={setPickedVersion}
          templateVersionLocked={hasTrial}
          onSelectUrs={onSelect('urs')}
          onSelectTrs={onSelect('trs')}
          onSelectRolesMatrix={onSelect('roles-matrix')}
          onSelectRandomizationAddendum={onSelect('randomization-addendum')}
          onRemoveUrs={() => remove.mutate({ slot: 'urs', version })}
          onRemoveTrs={() => remove.mutate({ slot: 'trs', version })}
          onRemoveRolesMatrix={() => remove.mutate({ slot: 'roles-matrix', version })}
          onRemoveRandomizationAddendum={() =>
            remove.mutate({ slot: 'randomization-addendum', version })
          }
          onToggleRandomization={(enabled) => {
            if (hasTrial) setRandomization.mutate({ enabled, version });
          }}
          onProcess={() =>
            start.mutate(undefined, {
              onSuccess: () => void navigate({ to: '/verification', search: { trialId } }),
            })
          }
        />
      </UploadPage>
    </AppShell>
  );
}
